import React from 'react';
import { motion } from 'framer-motion';
import { useNavigation } from '../NavigationContext';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

  .cert-page * { box-sizing: border-box; margin: 0; padding: 0; }
  .cert-page { font-family: 'Inter', sans-serif; color: #0c1a36; background: #ffffff; width: 100%; }

  .cert-breadcrumb { max-width: 1200px; margin: 0 auto; padding: 24px 24px 0; display: flex; align-items: center; gap: 8px; font-size: 13px; color: #94a3b8; }
  .cert-breadcrumb-btn { background: none; border: none; cursor: pointer; color: #1e4d8c; font-size: 13px; font-weight: 600; padding: 0; display: inline-flex; align-items: center; gap: 5px; transition: color 0.2s; }
  .cert-breadcrumb-btn:hover { color: #c9931a; }

  /* ── Hero ── */
  .cert-hero { position: relative; padding: 60px 24px 100px; overflow: hidden; background: #fff; }
  .cert-hero-blob1 { position: absolute; top: -8%; right: -4%; width: 540px; height: 540px; border-radius: 50%; background: radial-gradient(circle, rgba(30,77,140,0.26), rgba(201,147,26,0.14)); filter: blur(80px); pointer-events: none; }
  .cert-hero-blob2 { position: absolute; bottom: -12%; left: -6%; width: 580px; height: 580px; border-radius: 50%; background: radial-gradient(circle, rgba(12,26,54,0.16), rgba(5,150,105,0.08)); filter: blur(80px); pointer-events: none; }
  .cert-hero-inner { max-width: 1200px; margin: 0 auto; position: relative; z-index: 1; }
  .cert-hero-text { max-width: 840px; margin: 0 auto; text-align: center; }

  .cert-badge { display: inline-flex; align-items: center; gap: 8px; padding: 8px 18px; border-radius: 999px; background: #fef3c7; border: 1px solid #fde68a; font-size: 12.5px; font-weight: 600; color: #92400e; margin-bottom: 28px; }
  .cert-hero h1 { font-size: clamp(1.875rem, 3.75vw, 3rem); font-weight: 900; line-height: 1.08; letter-spacing: -0.03em; color: #0c1a36; margin-bottom: 22px; }
  .cert-gradient { background: linear-gradient(135deg, #1e4d8c 0%, #c9931a 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
  .cert-hero-sub { font-size: 1.15rem; color: #475569; line-height: 1.8; margin-bottom: 36px; }
  .cert-btn-group { display: flex; gap: 14px; flex-wrap: wrap; justify-content: center; margin-bottom: 52px; }
  .cert-btn-primary { padding: 14px 28px; background: #0c1a36; color: #fff; border: none; border-radius: 12px; font-size: 15px; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 8px; transition: background 0.2s, transform 0.15s; }
  .cert-btn-primary:hover { background: #1e4d8c; transform: translateY(-1px); }
  .cert-btn-secondary { padding: 14px 28px; background: #fff; color: #334155; border: 1px solid #e2e8f0; border-radius: 12px; font-size: 15px; font-weight: 600; cursor: pointer; transition: all 0.2s; }
  .cert-btn-secondary:hover { border-color: #fde68a; background: #fffbeb; color: #92400e; }

  /* Cert pills hero */
  .cert-pills { display: flex; flex-wrap: wrap; justify-content: center; gap: 12px; }
  .cert-pill { display: flex; align-items: center; gap: 8px; padding: 10px 18px; border-radius: 999px; border: 1.5px solid; font-size: 13px; font-weight: 600; }
  .cert-pill.sb { background: #eff6ff; border-color: #bfdbfe; color: #1e4d8c; }
  .cert-pill.vosb { background: #f0fdf4; border-color: #bbf7d0; color: #166534; }
  .cert-pill.sdvosb { background: #fef3c7; border-color: #fde68a; color: #92400e; }
  .cert-pill.wosb { background: #fdf4ff; border-color: #e9d5ff; color: #7e22ce; }
  .cert-pill.hub { background: #fff7ed; border-color: #fed7aa; color: #9a3412; }
  .cert-pill.eight { background: #f0fdfa; border-color: #99f6e4; color: #0f766e; }

  /* ── Shared ── */
  .cert-section { padding: 88px 24px; }
  .cert-container { max-width: 1200px; margin: 0 auto; }
  .cert-section-header { text-align: center; max-width: 720px; margin: 0 auto 60px; }
  .cert-section-header h2 { font-size: clamp(1.8rem, 3.2vw, 2.7rem); font-weight: 800; color: #0c1a36; letter-spacing: -0.02em; margin-bottom: 14px; line-height: 1.2; }
  .cert-section-header p { font-size: 1.03rem; color: #64748b; line-height: 1.8; }
  .cert-kicker { font-size: 12px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: #1e4d8c; margin-bottom: 12px; }

  /* ── Stats bar ── */
  .cert-stats-bar { background: #f8fafc; border-top: 1px solid rgba(226,232,240,0.7); border-bottom: 1px solid rgba(226,232,240,0.7); }
  .cert-stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); max-width: 1200px; margin: 0 auto; }
  @media (max-width: 768px) { .cert-stats-grid { grid-template-columns: 1fr 1fr; } }
  .cert-stat { padding: 36px 20px; text-align: center; border-right: 1px solid #f1f5f9; }
  .cert-stat:last-child { border-right: none; }
  @media (max-width: 768px) { .cert-stat:nth-child(2n) { border-right: none; } .cert-stat { border-bottom: 1px solid #f1f5f9; } }
  .cert-stat-num { font-size: 2.2rem; font-weight: 900; color: #1e4d8c; letter-spacing: -0.03em; line-height: 1; margin-bottom: 7px; }
  .cert-stat-label { font-size: 0.82rem; color: #64748b; font-weight: 500; line-height: 1.4; }

  /* ── Certification Cards ── */
  .cert-cards-bg { background: #fff; }
  .cert-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
  @media (max-width: 960px) { .cert-grid { grid-template-columns: 1fr 1fr; } }
  @media (max-width: 580px) { .cert-grid { grid-template-columns: 1fr; } }
  .cert-card { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 22px; padding: 32px 28px; transition: all 0.3s; position: relative; overflow: hidden; }
  .cert-card:hover { background: #fff; box-shadow: 0 18px 52px rgba(0,0,0,0.09); transform: translateY(-3px); border-color: transparent; }
  .cert-card::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px; border-radius: 22px 22px 0 0; opacity: 0; transition: opacity 0.3s; }
  .cert-card:hover::before { opacity: 1; }
  .cert-card.sb::before { background: linear-gradient(90deg, #1e4d8c, #3b82f6); }
  .cert-card.vosb::before { background: linear-gradient(90deg, #166534, #16a34a); }
  .cert-card.sdvosb::before { background: linear-gradient(90deg, #92400e, #d97706); }
  .cert-card.wosb::before { background: linear-gradient(90deg, #7e22ce, #a855f7); }
  .cert-card.hub::before { background: linear-gradient(90deg, #9a3412, #ea580c); }
  .cert-card.eight::before { background: linear-gradient(90deg, #0f766e, #0d9488); }
  .cert-card-badge { display: inline-flex; align-items: center; gap: 6px; padding: 5px 12px; border-radius: 999px; font-size: 11px; font-weight: 700; margin-bottom: 18px; }
  .cert-card-badge.sb { background: #eff6ff; color: #1e4d8c; border: 1px solid #bfdbfe; }
  .cert-card-badge.vosb { background: #f0fdf4; color: #166534; border: 1px solid #bbf7d0; }
  .cert-card-badge.sdvosb { background: #fef3c7; color: #92400e; border: 1px solid #fde68a; }
  .cert-card-badge.wosb { background: #fdf4ff; color: #7e22ce; border: 1px solid #e9d5ff; }
  .cert-card-badge.hub { background: #fff7ed; color: #9a3412; border: 1px solid #fed7aa; }
  .cert-card-badge.eight { background: #f0fdfa; color: #0f766e; border: 1px solid #99f6e4; }
  .cert-card h3 { font-size: 1.12rem; font-weight: 800; color: #0c1a36; margin-bottom: 10px; }
  .cert-card p { font-size: 0.89rem; color: #64748b; line-height: 1.72; margin-bottom: 18px; }
  .cert-card-reqs { display: flex; flex-direction: column; gap: 7px; padding-top: 16px; border-top: 1px solid #f1f5f9; }
  .cert-req { display: flex; align-items: center; gap: 8px; font-size: 12px; color: #475569; }
  .cert-req-dot { width: 16px; height: 16px; border-radius: 50%; background: #f1f5f9; display: flex; align-items: center; justify-content: center; font-size: 8px; color: #64748b; flex-shrink: 0; }
  .cert-card-spending { margin-top: 18px; padding: 12px 14px; border-radius: 12px; font-size: 12px; font-weight: 600; }
  .cert-card-spending.sb { background: #eff6ff; color: #1e4d8c; }
  .cert-card-spending.vosb { background: #f0fdf4; color: #166534; }
  .cert-card-spending.sdvosb { background: #fef3c7; color: #92400e; }
  .cert-card-spending.wosb { background: #fdf4ff; color: #7e22ce; }
  .cert-card-spending.hub { background: #fff7ed; color: #9a3412; }
  .cert-card-spending.eight { background: #f0fdfa; color: #0f766e; }

  /* ── Why Set-Asides Matter (dark) ── */
  .cert-dark-bg { background: #0c1a36; position: relative; overflow: hidden; }
  .cert-dark-glow { position: absolute; top: -8%; right: 15%; width: 500px; height: 500px; background: radial-gradient(circle, rgba(201,147,26,0.14), transparent 70%); pointer-events: none; }
  .cert-dark-bg .cert-kicker { color: #fbbf24; }
  .cert-dark-bg .cert-section-header h2 { color: #fff; }
  .cert-dark-bg .cert-section-header p { color: #94a3b8; }
  .cert-dark-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
  @media (max-width: 700px) { .cert-dark-grid { grid-template-columns: 1fr; } }
  .cert-dark-card { background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.08); border-radius: 18px; padding: 28px; display: flex; gap: 16px; align-items: flex-start; transition: all 0.3s; }
  .cert-dark-card:hover { border-color: rgba(201,147,26,0.35); background: rgba(255,255,255,0.07); }
  .cert-dark-icon { flex-shrink: 0; font-size: 24px; }
  .cert-dark-card h4 { font-size: 0.97rem; font-weight: 700; color: #fff; margin-bottom: 8px; }
  .cert-dark-card p { font-size: 0.87rem; color: #94a3b8; line-height: 1.68; }

  /* ── CTA ── */
  .cert-cta-bg { background: #fff; border-top: 1px solid #f1f5f9; }
  .cert-cta-inner { background: linear-gradient(135deg, #0c1a36 0%, #1a3568 60%, #0c1a36 100%); border-radius: 28px; padding: 80px 56px; text-align: center; position: relative; overflow: hidden; max-width: 900px; margin: 0 auto; }
  @media (max-width: 640px) { .cert-cta-inner { padding: 52px 28px; border-radius: 20px; } }
  .cert-cta-glow1 { position: absolute; top: -20%; right: -5%; width: 350px; height: 350px; background: radial-gradient(circle, rgba(201,147,26,0.22), transparent 65%); pointer-events: none; }
  .cert-cta-glow2 { position: absolute; bottom: -20%; left: -5%; width: 350px; height: 350px; background: radial-gradient(circle, rgba(30,77,140,0.3), transparent 65%); pointer-events: none; }
  .cert-cta-inner h2 { font-size: clamp(1.9rem, 3.5vw, 2.8rem); font-weight: 900; color: #fff; letter-spacing: -0.03em; line-height: 1.15; margin-bottom: 16px; position: relative; z-index: 1; }
  .cert-cta-inner > p { font-size: 1.05rem; color: #94a3b8; max-width: 520px; margin: 0 auto 36px; line-height: 1.75; position: relative; z-index: 1; }
  .cert-cta-btns { display: flex; gap: 14px; justify-content: center; flex-wrap: wrap; position: relative; z-index: 1; }
  .cert-cta-primary { padding: 14px 30px; background: #c9931a; color: #fff; border: none; border-radius: 12px; font-size: 15px; font-weight: 700; cursor: pointer; display: inline-flex; align-items: center; gap: 8px; transition: background 0.2s, transform 0.15s; box-shadow: 0 4px 18px rgba(201,147,26,0.4); }
  .cert-cta-primary:hover { background: #e0a820; transform: translateY(-1px); }
  .cert-cta-secondary { padding: 14px 30px; background: rgba(255,255,255,0.07); color: #cbd5e1; border: 1px solid rgba(255,255,255,0.15); border-radius: 12px; font-size: 15px; font-weight: 600; cursor: pointer; transition: all 0.2s; }
  .cert-cta-secondary:hover { background: rgba(255,255,255,0.13); color: #fff; }
`;

const fadeUp = { initial: { opacity: 0, y: 28 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: '-40px' }, transition: { duration: 0.55 } };
const stagger = (i: number) => ({ ...fadeUp, transition: { duration: 0.5, delay: i * 0.08 } });

const certCards = [
  {
    cls: 'sb', badge: 'Small Business',
    title: 'Small Business (SB)',
    desc: 'The foundational federal certification. Qualifying as a small business opens access to set-aside contracts exclusively reserved for small businesses — representing over $163B in annual federal spending.',
    reqs: ['Meet SBA size standards for your NAICS code', 'U.S.-based and independently owned', 'Annual revenue or employee count within SBA limits'],
    spending: '💰 $163B+ in annual small business set-asides',
  },
  {
    cls: 'vosb', badge: 'Veteran-Owned',
    title: 'Veteran-Owned Small Business (VOSB)',
    desc: 'Reserved for small businesses at least 51% owned and controlled by one or more U.S. military veterans. Provides access to VA set-aside contracts and veteran preference in federal procurement.',
    reqs: ['51% owned and controlled by U.S. veteran(s)', 'Must meet small business size standards', 'Verified through VA SDVOSB/VOSB portal or SBA'],
    spending: '🎖️ Preferred status in VA and DoD procurement',
  },
  {
    cls: 'sdvosb', badge: 'Service-Disabled Veteran',
    title: 'Service-Disabled Veteran-Owned (SDVOSB)',
    desc: 'Strongest veteran preference in federal contracting. SDVOSBs receive sole-source awards and set-asides across all federal agencies, with mandatory VA goals of 12% of eligible contract spending.',
    reqs: ['51% owned by service-disabled veteran(s)', 'VA disability rating required', 'SBA certification or VA verification required'],
    spending: '🏅 12% VA mandatory goal — hundreds of millions in sole-source awards',
  },
  {
    cls: 'wosb', badge: 'Woman-Owned',
    title: 'Woman-Owned Small Business (WOSB)',
    desc: 'Set-asides for industries where women-owned businesses are underrepresented. Federal agencies have a 5% spending goal for WOSB contracts across all departments.',
    reqs: ['51% owned and controlled by U.S. women', 'Must meet small business size standards', 'SBA WOSB certification required'],
    spending: '👩‍💼 5% federal spending goal — $26B+ annually',
  },
  {
    cls: 'hub', badge: 'HUBZone',
    title: 'Historically Underutilized Business Zone (HUBZone)',
    desc: 'For businesses located in and hiring from historically underutilized geographic zones. Provides 10% price evaluation preference and set-aside access with a 3% federal spending goal.',
    reqs: ['Principal office in a certified HUBZone', '35% of employees must reside in a HUBZone', 'SBA HUBZone certification required'],
    spending: '📍 10% price preference + 3% federal spending goal',
  },
  {
    cls: 'eight', badge: '8(a) Program',
    title: 'SBA 8(a) Business Development',
    desc: 'A 9-year business development program for socially and economically disadvantaged small businesses. Provides sole-source contract authority up to $4.5M for services and $7M for manufacturing.',
    reqs: ['Socially and economically disadvantaged owner', 'Net worth below $750K (excluding business and home)', 'SBA 8(a) application and approval required'],
    spending: '🚀 Sole-source authority up to $7M — $10B+ in annual 8(a) spending',
  },
];

const whyCards = [
  { icon: '🎯', title: 'Set-Asides Remove Competition', desc: 'Set-aside contracts are only open to certified businesses. Instead of competing against thousands of large companies, you compete against a far smaller pool.' },
  { icon: '💰', title: 'Sole-Source Awards Are Possible', desc: 'SDVOSB, 8(a), and other certifications allow contracting officers to award contracts directly to your business without any competition at all.' },
  { icon: '📊', title: 'Agencies Have Mandatory Goals', desc: 'Every federal agency is required by law to award a percentage of its contracting dollars to certified small and disadvantaged businesses. They actively seek certified vendors.' },
  { icon: '⚡', title: "Certification Stacks Work in Your Favor", desc: 'Holding multiple certifications — e.g., SDVOSB + HUBZone — makes your business eligible for a wider range of set-asides and gives you compounding advantages.' },
];

const Certifications: React.FC = () => {
  const { navigateTo } = useNavigation();

  return (
    <>
      <style>{styles}</style>
      <div className="cert-page">

        <div className="cert-breadcrumb">
          <button className="cert-breadcrumb-btn" onClick={() => navigateTo('')}>← SAM.gov Registration</button>
          <span>/</span>
          <span>Certifications</span>
        </div>

        {/* Hero */}
        <section className="cert-hero">
          <div className="cert-hero-blob1" />
          <div className="cert-hero-blob2" />
          <div className="cert-hero-inner">
            <motion.div className="cert-hero-text" {...fadeUp}>
              <div className="cert-badge">🏅 Federal Set-Aside Certifications</div>
              <h1>
                Unlock Billions in<br />
                <span className="cert-gradient">Set-Aside Contracts</span>
              </h1>
              <p className="cert-hero-sub" style={{ maxWidth: 680, margin: '0 auto 36px' }}>
                Federal small business certifications give your company exclusive access to
                contracts that larger competitors cannot touch. We set up and optimize every
                certification your business qualifies for — right inside SAM.gov.
              </p>
              <div className="cert-btn-group">
                <button className="cert-btn-primary">Set Up My Certifications →</button>
                <button className="cert-btn-secondary">See What I Qualify For</button>
              </div>
              <div className="cert-pills">
                {[
                  { cls: 'sb', label: 'Small Business' },
                  { cls: 'vosb', label: 'Veteran-Owned (VOSB)' },
                  { cls: 'sdvosb', label: 'Service-Disabled (SDVOSB)' },
                  { cls: 'wosb', label: 'Woman-Owned (WOSB)' },
                  { cls: 'hub', label: 'HUBZone' },
                  { cls: 'eight', label: '8(a) Program' },
                ].map((p, i) => (
                  <span key={i} className={`cert-pill ${p.cls}`}>{p.label}</span>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Stats */}
        <section className="cert-stats-bar">
          <div className="cert-stats-grid">
            {[
              { num: '$163B+', label: 'Annual Small Business Set-Asides' },
              { num: '23%', label: 'Federal Spending Reserved for SB' },
              { num: '6', label: 'Certification Types We Handle' },
              { num: '100%', label: 'SAM.gov Profile Integration' },
            ].map((s, i) => (
              <motion.div key={i} className="cert-stat" {...stagger(i)}>
                <div className="cert-stat-num">{s.num}</div>
                <div className="cert-stat-label">{s.label}</div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Certification Cards */}
        <section className="cert-section cert-cards-bg">
          <div className="cert-container">
            <motion.div className="cert-section-header" {...fadeUp}>
              <div className="cert-kicker">Certifications We Handle</div>
              <h2>Every Federal Certification, Configured in SAM.gov</h2>
              <p>
                Federal certifications are set up through your SAM.gov Representations and
                Certifications section. Getting them right — and keeping them current — is
                essential to maintaining eligibility for set-aside opportunities.
              </p>
            </motion.div>
            <div className="cert-grid">
              {certCards.map((c, i) => (
                <motion.div key={i} className={`cert-card ${c.cls}`} {...stagger(i)}>
                  <div className={`cert-card-badge ${c.cls}`}>{c.badge}</div>
                  <h3>{c.title}</h3>
                  <p>{c.desc}</p>
                  <div className="cert-card-reqs">
                    {c.reqs.map((r, j) => (
                      <div key={j} className="cert-req">
                        <div className="cert-req-dot">✓</div>
                        <span>{r}</span>
                      </div>
                    ))}
                  </div>
                  <div className={`cert-card-spending ${c.cls}`}>{c.spending}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Set-Asides Matter */}
        <section className="cert-section cert-dark-bg">
          <div className="cert-dark-glow" />
          <div className="cert-container">
            <motion.div className="cert-section-header" {...fadeUp}>
              <div className="cert-kicker">Why It Matters</div>
              <h2>Why Certifications Change the Game</h2>
              <p>Federal certifications do not just add a badge to your profile — they fundamentally change how you compete for federal business.</p>
            </motion.div>
            <div className="cert-dark-grid">
              {whyCards.map((c, i) => (
                <motion.div key={i} className="cert-dark-card" {...stagger(i)}>
                  <div className="cert-dark-icon">{c.icon}</div>
                  <div>
                    <h4>{c.title}</h4>
                    <p>{c.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="cert-section cert-cta-bg">
          <div className="cert-container">
            <motion.div className="cert-cta-inner" {...fadeUp}>
              <div className="cert-cta-glow1" />
              <div className="cert-cta-glow2" />
              <h2>Find Out Which Certifications You Qualify For</h2>
              <p>Our specialists will review your business profile and configure every applicable certification in your SAM.gov registration — maximizing your set-aside eligibility from day one.</p>
              <div className="cert-cta-btns">
                <button className="cert-cta-primary">Set Up My Certifications →</button>
                <button className="cert-cta-secondary">Schedule a Free Consultation</button>
              </div>
            </motion.div>
          </div>
        </section>

      </div>
    </>
  );
};

export default Certifications;
